import { useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  addAdditionalProperties,
  classificationTypes,
  type TreatmentFromForm,
} from '@/components/Studies/Treatments/Treatment.utils';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import type { MetadataField } from '@/model/Metadata.model';
import type { Study, StudyApiId } from '@/model/Study.model';
import type { ClassificationType, Treatment, TreatmentApiId, TreatmentCreateV1 } from '@/model/Treatment.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { modalAction } from '@/utils/modal';

interface UseTreatmentModalProps {
  study?: Study;
  metadata?: Array<MetadataField>;
  type: ClassificationType;
}

interface UseTreatmentModalResult {
  addingTreatment: boolean;
  updatingTreatment: boolean;
  openTreatmentModal: (treatment?: Treatment) => Promise<boolean>;
}

/**
 * Owns the existing study-groups treatment form modal and its update flow so
 * other study views can open exactly the same editor.
 */
export const useTreatmentModal = ({ study, metadata = [], type }: UseTreatmentModalProps): UseTreatmentModalResult => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const [addingTreatment, setAddingTreatment] = useState<boolean>(false);
  const [updatingTreatment, setUpdatingTreatment] = useState<boolean>(false);
  const isMounted = useMountedState();

  const { invoke: createTreatmentRequest } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/treatments',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: true } },
  });
  const { invoke: updateTreatmentRequest } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/treatments/{treatmentApiId}',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: true } },
  });

  const saveTreatment = async (newTreatment: TreatmentCreateV1): Promise<void> => {
    if (isMounted()) {
      setAddingTreatment(true);
    }
    try {
      await createTreatmentRequest({ path: { studyApiId: (study?.api_id ?? '') as StudyApiId }, body: newTreatment });
      successToast(`Successfully created ${classificationTypes[type].key}`);
    } catch (error) {
      const inVivoError = new InVivoError(`Could not create new ${classificationTypes[type].key}`, {
        slug: 'treatments',
        cause: error,
      });
      ExceptionHandler.captureException(inVivoError);
      errorToast(`Could not create ${classificationTypes[type].key}`);
      return Promise.reject(inVivoError).finally(() => {
        if (isMounted()) {
          dispatch({ type: 'set-submitting', value: false });
        }
      });
    }
    if (isMounted()) {
      setAddingTreatment(false);
      closeModal();
    }
  };

  const updateTreatment = async (treatmentData: TreatmentCreateV1, treatmentApiId: TreatmentApiId): Promise<void> => {
    try {
      if (isMounted()) {
        setUpdatingTreatment(true);
      }
      await updateTreatmentRequest({
        body: treatmentData,
        path: { studyApiId: (study?.api_id ?? '') as StudyApiId, treatmentApiId },
      });
      successToast(`Successfully updated ${classificationTypes[type].key}`);
      if (isMounted()) {
        closeModal();
      }
    } catch (error) {
      const inVivoError = new InVivoError('Could not update treatment', { slug: 'treatments', cause: error });
      ExceptionHandler.captureException(inVivoError);
      errorToast('Could not update treatment');
      return Promise.reject(inVivoError).finally(() => {
        if (isMounted()) {
          dispatch({ type: 'set-submitting', value: false });
        }
      });
    }
    if (isMounted()) {
      setUpdatingTreatment(false);
    }
  };

  const onSubmit = async (
    formItems: TreatmentFromForm,
    edit: boolean,
    treatmentApiId: TreatmentApiId
  ): Promise<void> => {
    const classificationType = formItems.treatment_classification ?? 'treatment';
    const treatmentData = addAdditionalProperties(formItems, metadata, classificationType);

    if (edit) {
      await updateTreatment(treatmentData, treatmentApiId);
    } else {
      await saveTreatment(treatmentData);
    }
  };

  const openTreatmentModal = (treatment?: Treatment): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      openModal('ADD_TREATMENTS_FORM', {
        treatment: _notNil(treatment) ? treatment : null,
        study,
        metadata,
        onSubmit: (formItems: TreatmentFromForm, edit: boolean, treatmentApiId: TreatmentApiId) =>
          onSubmit(formItems, edit, treatmentApiId)
            .then(() => resolve(true))
            .catch(() => resolve(false)),
        onCancel: () => {
          closeModal();
          resolve(false);
        },
        classificationType: type,
      });
    });

  return { addingTreatment, updatingTreatment, openTreatmentModal };
};
